/**
 * LodgeForm (13-24): the office lodge for all five registers, built on the field set.
 *
 * - **What it renders:** FIELDSET §3.2 per register (Incidents 26, Complaints 16, Feedback 13, Risk 4, CI 8), from
 *   `config/fieldset/registerFields` in `lib/lodgeLayout`'s sections. Labels, choices, caps and required rules are
 *   the field set's; nothing is retyped here, and no SharePoint "required" attribute stands in for the form's rules
 *   (FIELDSET §1.1).
 * - **What it sends:** `submitLodge` → `buildLodge` → the Writer. The builder writes only the §3.2 columns plus
 *   `Status` = `New` and `ClosureStatus` = `Open`; no triage rating and no other floor column can be sent (§1.3).
 *   A field hidden by its condition is cleared and never sent. No answer is defaulted (§1.1): `ReportedOn` is the
 *   one pre-fill FIELDSET names, shown as such, and dates have an explicit "Today" button, never a silent default.
 * - **Who:** `ReportedByEmail` (Incidents) / `RaisedByUPN` (CI) are the signed-in UPN, shown read-only (D-13-L-8).
 * - **Refusals** sit amber at their field and in a summary at the top, and focus moves to the first (D-13-24-8).
 *   Warnings ask once, then the person may lodge as it is.
 * - **After:** the ref, "Open the record" (`/record/<ref>`, 13-25), and the attachments line linking the item's
 *   SharePoint form (D-13-L-6). Owed audit rows read "Saved; N audit rows will be posted", never "done" (D-13-L-5).
 * Teal primary, purple identity only, amber never red for a question to answer (D-13-02-1, D-13-15-1/4).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Input, Select, TextArea } from '../ui';
import { canDo } from '../../config/roles';
import { choicesOf, lodgeColumns } from '../../config/fieldset/registerFields';
import type { FieldSpec } from '../../config/fieldset/registerFields';
import { formatDay, formatInstant, melbourneWallToInstant, todayMelbourne } from '../../lib/fieldValues';
import { lodgeConditions } from '../../lib/lodgeBuilder';
import {
  EMPTY_FORM,
  INJURED_WORKER_NAME,
  PICKER_COLUMNS,
  REPORTED_ON,
  WHISTLEBLOWER_COLUMN,
  clearHidden,
  formColumns,
  formRefusals,
  isRequiredNow,
  isVisible,
  lodgeFlags,
  lodgeSections,
  melbourneTimeOf,
  specOf,
  timeKey,
  toLodgeValues,
} from '../../lib/lodgeLayout';
import type { LodgeFormState } from '../../lib/lodgeLayout';
import { submitLodge } from '../../services/lodgeService';
import type { SubmitOutcome } from '../../services/lodgeService';
import type { Writer } from '../../services/commitAdapter';
import type { RecordIO } from '../../services/recordService';
import type { ClientEntry, ClientSearchResult } from '../../services/clientDirectory';
import type { Actor } from '../../types/actionRegister';
import type { AppRole } from '../../types/auth';
import type { RecordRegister, Refusal } from '../../types/records';
import ClientPicker from './ClientPicker';
import styles from './LodgeForm.module.css';

// ---------------------------------------------------------------------------
// The register tiles
// ---------------------------------------------------------------------------

const TILES: ReadonlyArray<{ register: RecordRegister; title: string; hint: string; noun: string }> = [
  { register: 'incidents', title: 'An incident', hint: 'Something happened, or is alleged or suspected.', noun: 'incident' },
  { register: 'complaints', title: 'A complaint', hint: 'Someone is unhappy with a service or how it was given.', noun: 'complaint' },
  { register: 'feedback', title: 'Feedback', hint: 'A compliment, a suggestion or a comment.', noun: 'feedback' },
  { register: 'risk', title: 'A risk or hazard', hint: 'Something that could cause harm or loss if nothing changes.', noun: 'risk' },
  { register: 'ci', title: 'An improvement', hint: 'A better way of doing something.', noun: 'improvement' },
];

const NOUN = Object.fromEntries(TILES.map((t) => [t.register, t.noun])) as Record<RecordRegister, string>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LodgeFormProps {
  /** The signed-in person; `null` while their identity resolves. */
  actor: Actor | null;
  roles: AppRole[];
  writer: Writer;
  /** `null` until the Q&S site resolves. */
  io: RecordIO | null;
  siteError: string | null;
  /** The client directory search, bound to the signed-in Graph client. */
  searchClients: (stream: string, query: string) => Promise<ClientSearchResult>;
}

type Lodged = Extract<SubmitOutcome, { kind: 'lodged' }>;

const fmt = (n: number) => n.toLocaleString('en-AU');

/** The echo beside a date input: `D MMM YYYY`, plus the Melbourne time when one is entered (D-13-L-10). */
function dateEcho(day: string | undefined, time: string | undefined): string | null {
  if (!day) return null;
  if (time) {
    const r = melbourneWallToInstant(day, time);
    return r.ok ? formatInstant(r.value) : null;
  }
  const label = formatDay(day);
  return label === '—' ? null : label;
}

// ---------------------------------------------------------------------------
// The form
// ---------------------------------------------------------------------------

export default function LodgeForm({ actor, roles, writer, io, siteError, searchClients }: LodgeFormProps) {
  const lodgeable = useMemo(() => TILES.filter((t) => canDo(roles, t.register, 'lodge')), [roles]);

  const [register, setRegister] = useState<RecordRegister | null>(null);
  const [form, setForm] = useState<LodgeFormState>(EMPTY_FORM);
  const [refusals, setRefusals] = useState<Refusal[]>([]);
  const [warnings, setWarnings] = useState<Refusal[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [lodged, setLodged] = useState<Lodged | null>(null);
  const [openSections, setOpenSections] = useState<ReadonlySet<string>>(new Set());
  const [focusToken, setFocusToken] = useState(0);
  const [today] = useState(() => todayMelbourne(new Date()));
  const summaryRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLHeadingElement>(null);

  // --- Messages per column --------------------------------------------------------------------------------------
  const refusalAt = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of refusals) {
      if (!r.column) continue;
      // The client id's refusals show under the picker, with the name's.
      const col = r.column === 'RecipientClientId' ? 'RecipientName' : r.column;
      if (!m.has(col)) m.set(col, r.message);
    }
    return m;
  }, [refusals]);
  const warningAt = useMemo(() => new Map(warnings.filter((w) => w.column).map((w) => [w.column as string, w.message])), [warnings]);

  // --- Focus the first refusal after a refused submit --------------------------------------------------------
  useEffect(() => {
    if (focusToken === 0 || !register) return;
    const first = formColumns(register).find((c) => refusalAt.has(c));
    const el = first ? document.getElementById(`lodge-${first}`) : null;
    if (el) el.focus();
    else summaryRef.current?.focus();
  }, [focusToken, register, refusalAt]);

  useEffect(() => {
    if (lodged) resultRef.current?.focus();
  }, [lodged]);

  // --- No register this role can lodge --------------------------------------------------------------------
  if (lodgeable.length === 0) {
    return (
      <Card variant="outlined">
        <p className={styles.lead}>Your role can't lodge to any register. If that seems wrong, check with Oversight.</p>
      </Card>
    );
  }

  const busy = submitting;
  const ready = Boolean(io && actor);

  function choose(next: RecordRegister) {
    if (next === register && !lodged) return;
    setRegister(next);
    setForm(EMPTY_FORM);
    setRefusals([]);
    setWarnings([]);
    setConfirming(false);
    setFailure(null);
    setLodged(null);
    setOpenSections(new Set());
  }

  function update(change: (s: LodgeFormState) => LodgeFormState) {
    if (!register) return;
    setForm((s) => clearHidden(register, change(s)));
    setConfirming(false);
  }

  /** A changed answer drops its own refusal (and its warning); the rest stay until the next lodge. */
  function settle(...columns: string[]) {
    const base = columns.map((c) => c.replace(/@time$/, ''));
    setRefusals((rs) => rs.filter((r) => !r.column || !base.includes(r.column)));
    setWarnings((ws) => ws.filter((w) => !w.column || !base.includes(w.column)));
  }

  const setValue = (column: string, value: string) => {
    settle(column, ...(column === 'RecipientName' ? ['RecipientClientId'] : []));
    update((s) => ({ ...s, values: { ...s.values, [column]: value } }));
  };

  function setBusinessUnit(value: string) {
    // A different business unit can mean different client lists: the picked client is cleared (D-13-24-4).
    settle('BusinessUnit', 'RecipientName', 'RecipientClientId');
    update((s) => {
      const values: Record<string, string> = { ...s.values, BusinessUnit: value };
      delete values.RecipientName;
      delete values.RecipientClientId;
      return { ...s, values, clientUnlisted: false };
    });
  }

  function pickClient(entry: ClientEntry | null) {
    settle('RecipientName', 'RecipientClientId');
    update((s) => {
      const values: Record<string, string> = { ...s.values };
      if (entry) {
        values.RecipientName = entry.name;
        values.RecipientClientId = entry.id;
      } else {
        delete values.RecipientName;
        delete values.RecipientClientId;
      }
      return { ...s, values };
    });
  }

  function setUnlisted(unlisted: boolean) {
    settle('RecipientName', 'RecipientClientId');
    update((s) => {
      const values: Record<string, string> = { ...s.values };
      delete values.RecipientName;
      delete values.RecipientClientId;
      return { ...s, values, clientUnlisted: unlisted };
    });
  }

  async function onSubmit(e: { preventDefault(): void }, acceptWarnings = false) {
    e.preventDefault();
    if (!register || !actor || !io) return;
    setSubmitting(true);
    setFailure(null);
    try {
      const outcome = await submitLodge(io, register, toLodgeValues(register, form), actor, {
        ...lodgeFlags(register, form),
        formRefusals: formRefusals(register, form),
        acceptWarnings,
        writer,
      });
      if (outcome.kind === 'refused') {
        setRefusals(outcome.refusals);
        setWarnings(outcome.warnings);
        setConfirming(false);
        setFocusToken((n) => n + 1);
      } else if (outcome.kind === 'confirm') {
        setRefusals([]);
        setWarnings(outcome.warnings);
        setConfirming(true);
      } else {
        setRefusals([]);
        setWarnings([]);
        setConfirming(false);
        setLodged(outcome);
      }
    } catch (err) {
      setFailure(err instanceof Error ? err.message : 'Something went wrong lodging this.');
    } finally {
      setSubmitting(false);
    }
  }

  function lodgeAnother() {
    setForm(EMPTY_FORM);
    setLodged(null);
    setRefusals([]);
    setWarnings([]);
    setOpenSections(new Set());
  }

  // --- The confirmation ------------------------------------------------------------------------------------
  const practice = lodged?.mode === 'practice';
  const owed = lodged?.auditPending.length ?? 0;
  const confirmation =
    register && lodged ? (
      <Card className={styles.done}>
        <p className={styles.doneKicker}>{practice ? 'Practice lodge' : 'Lodged'}</p>
        <h2 className={styles.doneRef} tabIndex={-1} ref={resultRef}>
          {lodged.ref}
        </h2>
        {practice ? (
          <p className={styles.doneLine}>
            <strong>Practice mode: nothing is saved.</strong> This practice {NOUN[register]} is kept only in this browser,
            so you can open it and try the next steps.
          </p>
        ) : owed > 0 ? (
          <p className={styles.doneLine}>
            Saved; {owed} audit {owed === 1 ? 'row' : 'rows'} will be posted. The record is in the register; its audit
            trail is finished automatically the next time the dashboard can reach SharePoint.
          </p>
        ) : (
          <p className={styles.doneLine}>It's in the register as a New record. A Care Manager picks it up from there.</p>
        )}
        <p className={styles.doneLine}>
          {lodged.editFormUrl ? (
            <a href={lodged.editFormUrl} target="_blank" rel="noreferrer">
              Add attachments on the SharePoint form
            </a>
          ) : (
            <span>Attachments are added on the record's SharePoint form. A practice record has none, so there is no link.</span>
          )}
          {register === 'incidents' && (
            <span className={styles.doneHint}>
              {' '}
              Name an investigation report <code>{lodged.ref}-investigation-report</code> and a WorkSafe written record{' '}
              <code>{lodged.ref}-worksafe-record</code>.
            </span>
          )}
        </p>
        <div className={styles.doneActions}>
          <Link className={styles.linkButton} to={`/record/${lodged.ref}`}>
            Open the record
          </Link>
          <Button variant="secondary" onClick={lodgeAnother}>
            Lodge another {NOUN[register]}
          </Button>
        </div>
      </Card>
    ) : null;

  // --- Field controls ------------------------------------------------------------------------------------------
  function renderField(column: string) {
    if (!register) return null;
    if (column === 'RecipientClientId') return null; // the picker renders it with the name
    if (!isVisible(register, column, form)) {
      if (column === WHISTLEBLOWER_COLUMN) {
        return (
          <label key="wb-tick" className={styles.tick}>
            <input
              type="checkbox"
              checked={false}
              disabled={busy}
              onChange={() => update((s) => ({ ...s, whistleblower: true }))}
            />
            <span>This is a whistleblower disclosure</span>
          </label>
        );
      }
      return null;
    }
    const spec = specOf(register, column);
    const id = `lodge-${column}`;
    const required = isRequiredNow(register, column, form);
    const message = refusalAt.get(column);
    const warning = warningAt.get(column);
    const value = form.values[column] ?? '';
    const msgId = message ? `${id}-msg` : undefined;
    const describedBy = [msgId, warning ? `${id}-warn` : undefined].filter(Boolean).join(' ') || undefined;
    const refusedClass = message ? styles.inputRefused : undefined;

    const notes = (
      <>
        {message && (
          <p id={msgId} className={styles.refusal}>
            {message}
          </p>
        )}
        {warning && !message && (
          <p id={`${id}-warn`} className={styles.notice}>
            {warning}
          </p>
        )}
      </>
    );

    if (PICKER_COLUMNS.includes(column)) {
      return (
        <ClientPicker
          key={`${column}-${form.values.BusinessUnit ?? ''}-${form.clientUnlisted}`}
          stream={form.values.BusinessUnit ?? ''}
          search={searchClients}
          label={spec.label}
          required={required}
          name={form.values.RecipientName ?? ''}
          clientId={form.values.RecipientClientId ?? ''}
          unlisted={form.clientUnlisted}
          onPick={pickClient}
          onUnlistedChange={setUnlisted}
          onUnlistedName={(n) => setValue('RecipientName', n)}
          message={message}
          disabled={busy}
          inputId={id}
        />
      );
    }

    const labelEl = (
      <label htmlFor={id} className={styles.label}>
        {spec.label}
        {required && <span className={styles.req} aria-hidden="true"> *</span>}
      </label>
    );

    if (spec.kind === 'choice') {
      return (
        <div key={column} className={styles.field}>
          {labelEl}
          <Select
            id={id}
            value={value}
            disabled={busy}
            className={refusedClass}
            aria-describedby={describedBy}
            aria-required={required || undefined}
            onChange={(e) => (column === 'BusinessUnit' ? setBusinessUnit(e.target.value) : setValue(column, e.target.value))}
          >
            <option value="">Choose…</option>
            {choicesOf(register, column).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          {column === WHISTLEBLOWER_COLUMN && (
            <button type="button" className={styles.textButton} onClick={() => update((s) => ({ ...s, whistleblower: false }))}>
              Not a whistleblower disclosure
            </button>
          )}
          {notes}
        </div>
      );
    }

    if (spec.kind === 'day-existing' || spec.kind === 'instant') {
      return renderDate(spec, id, required, labelEl, notes, describedBy, refusedClass);
    }

    const cap = spec.cap ?? 255;
    const over = value.length > cap;
    const count = (
      <span className={`${styles.count} ${over ? styles.countOver : ''}`} aria-live="polite">
        {fmt(value.length)} / {fmt(cap)}
      </span>
    );

    if (spec.kind === 'textarea') {
      return (
        <div key={column} className={styles.field}>
          {labelEl}
          <TextArea
            id={id}
            rows={4}
            value={value}
            disabled={busy}
            className={refusedClass}
            aria-describedby={describedBy}
            aria-required={required || undefined}
            onChange={(e) => setValue(column, e.target.value)}
          />
          <div className={styles.under}>{count}</div>
          {over && <p className={styles.notice}>That's over the {fmt(cap)}-character limit. It won't be cut short; please shorten it.</p>}
          {notes}
        </div>
      );
    }

    // text, position, upn
    // 13.2-09 (D-13.2-P-2): the injured worker by name. "That's me" fills the lodger's own name; an email address is
    // refused inline (`name-not-email`).
    const isName = column === INJURED_WORKER_NAME;
    const myName = actor?.displayName?.trim() ?? '';
    const hintId = isName ? `${id}-hint` : undefined;
    return (
      <div key={column} className={styles.field}>
        {labelEl}
        <div className={isName ? styles.inline : undefined}>
          <Input
            id={id}
            type={spec.kind === 'upn' ? 'email' : 'text'}
            value={value}
            disabled={busy}
            className={refusedClass}
            aria-describedby={[describedBy, hintId].filter(Boolean).join(' ') || undefined}
            aria-required={required || undefined}
            placeholder={isName ? 'Their full name' : undefined}
            autoComplete={isName ? 'off' : undefined}
            onChange={(e) => setValue(column, e.target.value)}
          />
          {isName && myName && (
            <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => setValue(column, myName)}>
              That&rsquo;s me
            </Button>
          )}
        </div>
        {isName && (
          <p id={hintId} className={styles.hint}>
            Their name, as ShiftCare or Harmony AP has it, never an email address.
          </p>
        )}
        {value.length > cap * 0.8 && <div className={styles.under}>{count}</div>}
        {notes}
      </div>
    );
  }

  function renderDate(
    spec: FieldSpec,
    id: string,
    required: boolean,
    labelEl: ReactNode,
    notes: ReactNode,
    describedBy: string | undefined,
    refusedClass: string | undefined,
  ) {
    const column = spec.name;
    const tKey = timeKey(column);
    const day = form.values[column] ?? '';
    const time = form.values[tKey] ?? '';
    const timed = spec.kind === 'instant' || spec.timeWhenKnown === true;

    if (column === REPORTED_ON && !form.reportedOnEdited) {
      return (
        <div key={column} className={styles.field}>
          <span className={styles.label} id={`${id}-label`}>
            {spec.label}
            {required && <span className={styles.req} aria-hidden="true"> *</span>}
          </span>
          <div className={styles.inline}>
            <span className={styles.prefill}>The moment you press Lodge</span>
            <Button
              id={id}
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              aria-describedby={`${id}-label`}
              onClick={() =>
                update((s) => {
                  const now = new Date();
                  return {
                    ...s,
                    reportedOnEdited: true,
                    values: { ...s.values, [column]: todayMelbourne(now), [tKey]: melbourneTimeOf(now) },
                  };
                })
              }
            >
              Change
            </Button>
          </div>
          {notes}
        </div>
      );
    }

    const echo = dateEcho(day, time);
    return (
      <div key={column} className={styles.field}>
        {labelEl}
        <div className={styles.dateRow}>
          <Input
            id={id}
            type="date"
            value={day}
            max={today}
            disabled={busy}
            className={`${styles.dateInput} ${refusedClass ?? ''}`}
            aria-describedby={[describedBy, `${id}-echo`].filter(Boolean).join(' ')}
            aria-required={required || undefined}
            onChange={(e) => setValue(column, e.target.value)}
          />
          {timed && (
            <Input
              id={`${id}-time`}
              type="time"
              value={time}
              disabled={busy}
              className={styles.timeInput}
              aria-label={spec.kind === 'instant' ? `${spec.label}: time` : `${spec.label}: time, if known`}
              onChange={(e) => setValue(tKey, e.target.value)}
            />
          )}
          {column !== REPORTED_ON && (
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setValue(column, today)}>
              Today
            </Button>
          )}
          {column === REPORTED_ON && (
            <button
              type="button"
              className={styles.textButton}
              onClick={() =>
                update((s) => {
                  const values: Record<string, string> = { ...s.values };
                  delete values[column];
                  delete values[tKey];
                  return { ...s, values, reportedOnEdited: false };
                })
              }
            >
              Use the moment I press Lodge
            </button>
          )}
        </div>
        <p id={`${id}-echo`} className={styles.hint}>
          {echo ?? (spec.kind === 'instant' ? 'Enter the date and the time.' : timed ? 'The date is required; add the time if you know it.' : ' ')}
        </p>
        {notes}
      </div>
    );
  }

  // --- The page -----------------------------------------------------------------------------------------------
  const identity = register ? lodgeConditions(register).identity : undefined;
  const sections = register ? lodgeSections(register) : [];
  const unplaced = refusals.filter((r) => !r.column || !formColumns(register ?? 'incidents').includes(r.column));
  const hasAttachments = register ? lodgeColumns(register).includes('Attachments') : false;
  const title = TILES.find((t) => t.register === register)?.noun ?? '';

  return (
    <div className={styles.wrap}>
      <section aria-labelledby="lodge-choose" className={styles.chooser}>
        <h2 id="lodge-choose" className={styles.sectionTitle}>
          What are you lodging?
        </h2>
        <div className={styles.tiles} role="radiogroup" aria-labelledby="lodge-choose">
          {lodgeable.map((t) => (
            <button
              key={t.register}
              type="button"
              role="radio"
              aria-checked={register === t.register}
              className={`${styles.tile} ${register === t.register ? styles.tileOn : ''}`}
              onClick={() => choose(t.register)}
              disabled={busy}
            >
              <span className={styles.tileTitle}>{t.title}</span>
              <span className={styles.tileHint}>{t.hint}</span>
            </button>
          ))}
        </div>
      </section>

      {siteError && (
        <p className={styles.failure} role="alert">
          Couldn't connect to the Quality &amp; Safety site: {siteError}
        </p>
      )}

      {confirmation}

      {register && !lodged && (
        <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
          {refusals.length > 0 && (
            <div className={styles.summary} ref={summaryRef} tabIndex={-1} role="alert" aria-labelledby="lodge-summary-title">
              <p id="lodge-summary-title" className={styles.summaryTitle}>
                {refusals.length === 1 ? 'One thing to fix before this can be lodged:' : `${refusals.length} things to fix before this can be lodged:`}
              </p>
              <ul className={styles.summaryList}>
                {refusals.map((r, i) => (
                  <li key={`${r.code}-${r.column ?? ''}-${i}`}>
                    {r.column && formColumns(register).includes(r.column === 'RecipientClientId' ? 'RecipientName' : r.column) ? (
                      <a
                        href={`#lodge-${r.column === 'RecipientClientId' ? 'RecipientName' : r.column}`}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(`lodge-${r.column === 'RecipientClientId' ? 'RecipientName' : r.column}`)?.focus();
                        }}
                      >
                        {r.message}
                      </a>
                    ) : (
                      r.message
                    )}
                  </li>
                ))}
              </ul>
              {unplaced.length > 0 && <p className={styles.hint}>Anything not linked here isn't a field you can change.</p>}
            </div>
          )}

          {sections.map((section) => {
            const fields = section.columns.map((c) => renderField(c)).filter(Boolean);
            if (fields.length === 0) return null;
            if (section.collapsed) {
              const open =
                openSections.has(section.id) || section.columns.some((c) => refusalAt.has(c) || warningAt.has(c));
              return (
                <details
                  key={section.id}
                  className={styles.section}
                  open={open}
                  onToggle={(e) => {
                    const isOpen = (e.currentTarget as HTMLDetailsElement).open;
                    setOpenSections((prev) => {
                      const next = new Set(prev);
                      if (isOpen) next.add(section.id);
                      else next.delete(section.id);
                      return next;
                    });
                  }}
                >
                  <summary className={styles.sectionSummary}>{section.title}</summary>
                  <div className={styles.sectionBody}>{fields}</div>
                </details>
              );
            }
            return (
              <fieldset key={section.id} className={styles.section}>
                <legend className={styles.sectionTitle}>{section.title}</legend>
                <div className={styles.sectionBody}>{fields}</div>
              </fieldset>
            );
          })}

          <div className={styles.meta}>
            {identity && actor && (
              <p className={styles.reportedBy}>
                <span className={styles.metaLabel}>{specOf(register, identity).label}:</span> {actor.displayName || actor.upn}{' '}
                <span className={styles.metaMuted}>({actor.upn})</span>
              </p>
            )}
            {hasAttachments && (
              <p className={styles.metaMuted}>Attachments are added on the record's SharePoint form after you lodge.</p>
            )}
            <p className={styles.floorNote}>
              This lodges a <strong>New</strong> record only. Triage, reportability, approval and closure are recorded
              later, each by the person who owns that step.
            </p>
          </div>

          {confirming && warnings.length > 0 && (
            <div className={styles.confirm} role="status">
              <p className={styles.summaryTitle}>Before you lodge</p>
              <ul className={styles.summaryList}>
                {warnings.map((w, i) => (
                  <li key={`${w.code}-${i}`}>{w.message}</li>
                ))}
              </ul>
              <p className={styles.hint}>You can change it, or lodge it as it is.</p>
            </div>
          )}

          {failure && (
            <p className={styles.failure} role="alert">
              Couldn't lodge that: {failure}
            </p>
          )}

          <div className={styles.actions}>
            {!ready && <span className={styles.metaMuted}>Connecting to SharePoint…</span>}
            {confirming ? (
              <>
                <Button type="button" variant="secondary" disabled={busy} onClick={() => setConfirming(false)}>
                  Change it
                </Button>
                <Button type="button" loading={busy} disabled={!ready} onClick={(e) => void onSubmit(e, true)}>
                  Lodge as it is
                </Button>
              </>
            ) : (
              <Button type="submit" loading={busy} disabled={!ready}>
                Lodge {title}
              </Button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
